import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Shield, FileText, ArrowLeft, Save, Edit2, RefreshCw, User, Building2,
  DollarSign, Clock, CheckCircle, Send, Globe, Circle, XCircle, AlertTriangle,
} from 'lucide-react';

const ESTADO_CONFIG = {
  BORRADOR: { color: 'bg-slate-100 text-slate-700', icon: FileText, label: 'Borrador' },
  ENVIANDO_UIF: { color: 'bg-indigo-100 text-indigo-700', icon: RefreshCw, label: 'Enviando a UIF...' },
  EN_REVISION: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'En Revisión' },
  PENDIENTE_APROBACION: { color: 'bg-purple-100 text-purple-700', icon: Clock, label: 'Pendiente Aprobación' },
  APROBADO: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Aprobado' },
  ENVIADO: { color: 'bg-blue-100 text-blue-700', icon: Send, label: 'Enviado a UIF' },
};

const Section = ({ title, icon: Icon, children }) => (
  <Card className="p-5">
    <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2 uppercase tracking-wider">
      <Icon size={16} className="text-slate-500" /> {title}
    </h3>
    {children}
  </Card>
);

const Field = ({ label, value, editing, onChange, type = 'text', className = '' }) => (
  <div className={className}>
    <Label className="text-xs text-slate-400 block mb-0.5">{label}</Label>
    {editing ? (
      type === 'textarea' ? (
        <Textarea className="text-sm min-h-[80px]" value={value || ''} onChange={e => onChange(e.target.value)} />
      ) : (
        <Input className="h-8 text-sm" value={value || ''} onChange={e => onChange(e.target.value)} />
      )
    ) : (
      type === 'textarea' ? (
        <p className="text-sm text-slate-700 bg-slate-50 rounded p-2 min-h-[60px] whitespace-pre-wrap">{value || <span className="text-slate-300 italic">Sin contenido</span>}</p>
      ) : (
        <p className="text-sm font-medium text-slate-900">{value || '-'}</p>
      )
    )}
  </div>
);

const STEP_ICON = {
  pending: { Icon: Circle, cls: 'text-slate-300' },
  running: { Icon: RefreshCw, cls: 'text-indigo-500 animate-spin' },
  done: { Icon: CheckCircle, cls: 'text-green-500' },
  error: { Icon: XCircle, cls: 'text-red-500' },
};

const UifProgressPanel = ({ progreso }) => {
  if (!progreso) return null;
  const isError = progreso.estado === 'error';
  const isDone = progreso.estado === 'done';
  return (
    <Card
      className={`p-5 border-2 ${isError ? 'border-red-200 bg-red-50/40' : isDone ? 'border-green-200 bg-green-50/40' : 'border-indigo-200 bg-indigo-50/40'}`}
      data-testid="uif-progress-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        {isError ? <AlertTriangle size={16} className="text-red-500" />
          : isDone ? <CheckCircle size={16} className="text-green-600" />
          : <RefreshCw size={16} className="text-indigo-500 animate-spin" />}
        <h3 className="text-sm font-bold text-slate-800">Envío a UIF — Progreso en vivo</h3>
      </div>
      <p className={`text-sm mb-4 ${isError ? 'text-red-600 font-medium' : 'text-slate-600'}`} data-testid="uif-progress-mensaje">
        {progreso.mensaje}
      </p>
      <div className="space-y-2.5">
        {(progreso.pasos || []).map((p) => {
          const cfg = STEP_ICON[p.estado] || STEP_ICON.pending;
          const I = cfg.Icon;
          return (
            <div key={p.key} className="flex items-start gap-3" data-testid={`uif-step-${p.key}`}>
              <I size={16} className={`${cfg.cls} mt-0.5 shrink-0`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${p.estado === 'error' ? 'text-red-600' : p.estado === 'done' ? 'text-slate-600' : 'text-slate-800'}`}>{p.label}</p>
                {p.detalle && (
                  <p className={`text-xs mt-0.5 break-words ${p.estado === 'error' ? 'text-red-500' : 'text-slate-400'}`} data-testid={`uif-step-detalle-${p.key}`}>
                    {p.detalle}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default function ROSDetalle() {
  const { rosId } = useParams();
  const navigate = useNavigate();
  const [ros, setRos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [uifForm, setUifForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [sendingUif, setSendingUif] = useState(false);
  const [uifProgreso, setUifProgreso] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const handleSaveRosTitle = async () => {
    const t = (titleValue || '').trim();
    if (t.length < 3) { toast.error('El título debe tener al menos 3 caracteres'); return; }
    setSavingTitle(true);
    try {
      const res = await api.patch(`/ros/${ros.id}/titulo`, { titulo: t });
      setRos(res.data);
      setEditingTitle(false);
      toast.success('Título actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el título');
    } finally { setSavingTitle(false); }
  };

  const loadRos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ros/${rosId}`);
      setRos(res.data);
      setUifProgreso(res.data.uif_progreso || null);
    } catch { toast.error('Error cargando ROS'); navigate(-1); }
    finally { setLoading(false); }
  }, [rosId, navigate]);

  useEffect(() => { loadRos(); }, [loadRos]);

  const startEdit = () => {
    setUifForm(JSON.parse(JSON.stringify(ros.formulario_uif || {})));
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/ros/${rosId}/formulario-uif`, uifForm);
      setRos(res.data);
      setEditing(false);
      toast.success('Formulario guardado');
    } catch { toast.error('Error guardando'); }
    finally { setSaving(false); }
  };

  const updateField = (section, field, value) => {
    setUifForm(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };

  const handleSendToUif = async () => {
    if (!window.confirm('¿Enviar este ROS como borrador al portal UIF?\nEl proceso toma aproximadamente 1 minuto.')) return;
    setSendingUif(true);
    setUifProgreso(null);
    try {
      const res = await api.post(`/ros/${rosId}/enviar-uif`);
      if (res.data.success) {
        toast.success(res.data.message || 'Envío iniciado');
        // Poll de progreso en vivo (cada 2.5s)
        const pollInterval = setInterval(async () => {
          try {
            const prog = await api.get(`/ros/${rosId}/uif-progreso`);
            if (prog.data.uif_progreso) setUifProgreso(prog.data.uif_progreso);
            const estado = prog.data.estado;
            if (estado !== 'ENVIANDO_UIF') {
              clearInterval(pollInterval);
              setSendingUif(false);
              await loadRos();
              if (estado === 'EN_REVISION') {
                const nro = prog.data.nro_operacion_uif || '';
                toast.success(`ROS cargado en UIF${nro ? ` (Nro: ${nro})` : ''}`);
              } else if (estado === 'BORRADOR') {
                toast.error(prog.data.ultimo_detalle || 'Error al enviar a UIF');
              }
            }
          } catch { /* ignore poll errors */ }
        }, 2500);
        // Timeout after 4 minutes
        setTimeout(() => { clearInterval(pollInterval); setSendingUif(false); }, 240000);
      } else {
        toast.error(`Error: ${res.data.message}`);
        setSendingUif(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando a UIF');
      setSendingUif(false);
    }
  };

  const getVal = (section, field) => {
    const source = editing ? uifForm : (ros?.formulario_uif || {});
    return (source[section] || {})[field] || '';
  };

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw size={32} className="animate-spin text-slate-300" /></div>;
  if (!ros) return null;

  const fuif = ros.formulario_uif || {};
  const hasPF = Object.keys(fuif.persona_fisica || {}).length > 0;
  const hasPJ = Object.keys(fuif.persona_juridica || {}).length > 0;
  const hasPFE = Object.keys(fuif.persona_fisica_extranjera || {}).length > 0;
  const estadoCfg = ESTADO_CONFIG[ros.estado] || ESTADO_CONFIG.BORRADOR;
  const EstadoIcon = estadoCfg.icon;

  return (
    <div className="space-y-5 pb-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="shrink-0">
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Shield size={20} className="text-purple-600" />
              {ros.id}
            </h1>
            {editingTitle ? (
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  className="h-8 w-[280px] sm:w-[360px] text-sm"
                  placeholder="Nombre del ROS"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRosTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                  data-testid="ros-title-edit-input"
                />
                <Button size="sm" className="h-8 px-2" onClick={handleSaveRosTitle} disabled={savingTitle} data-testid="ros-title-save-btn">
                  <CheckCircle size={15} />
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setEditingTitle(false)} disabled={savingTitle} data-testid="ros-title-cancel-btn">
                  <XCircle size={15} />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-slate-700 font-medium flex items-center gap-2 group mt-0.5" data-testid="ros-title">
                {ros.titulo || <span className="text-slate-400 italic font-normal">Sin nombre</span>}
                <button
                  onClick={() => { setTitleValue(ros.titulo || ''); setEditingTitle(true); }}
                  title="Editar nombre del ROS"
                  className="opacity-60 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-purple-600"
                  data-testid="ros-title-edit-btn"
                >
                  <Edit2 size={14} />
                </button>
              </p>
            )}
            <p className="text-xs text-slate-400">{ros.caso_titulo} {ros.razon_social ? `— ${ros.razon_social}` : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Badge className={`${estadoCfg.color} flex items-center gap-1`}>
            <EstadoIcon size={12} className={ros.estado === 'ENVIANDO_UIF' ? 'animate-spin' : ''} /> {estadoCfg.label}
          </Badge>
          {ros.nro_operacion_uif && (
            <Badge className="bg-green-100 text-green-700">
              <Globe size={12} className="mr-1" /> UIF Nro: {ros.nro_operacion_uif}
            </Badge>
          )}
          {!editing ? (
            <>
              <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-uif-btn">
                <Edit2 size={14} className="mr-1" /> Editar
              </Button>
              {(ros.estado === 'BORRADOR' || ros.estado === 'RECHAZADO' || ros.estado === 'ENVIANDO_UIF') && (
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleSendToUif} disabled={sendingUif || ros.estado === 'ENVIANDO_UIF'} data-testid="send-uif-btn">
                  {(sendingUif || ros.estado === 'ENVIANDO_UIF') ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                  {(sendingUif || ros.estado === 'ENVIANDO_UIF') ? 'Enviando a UIF...' : 'Enviar a UIF'}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="save-uif-btn">
                {saving ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
                Guardar
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancelar</Button>
            </>
          )}
        </div>
      </div>

      {/* Panel de progreso del envío a UIF */}
      <UifProgressPanel progreso={uifProgreso} />

      {/* Datos de la Operación */}
      <Section title="Datos de la Operación" icon={Globe}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Tipo Operación" value={getVal('datos_operacion', 'tipo_operacion') === '1' ? 'Lavado de Activos (ROS)' : getVal('datos_operacion', 'tipo_operacion')} editing={false} />
          <Field label="Operación" value={getVal('datos_operacion', 'operacion')} editing={editing} onChange={v => updateField('datos_operacion', 'operacion', v)} />
          <Field label="Exteriorización Voluntaria" value={getVal('datos_operacion', 'exteriorizacion_voluntaria')} editing={editing} onChange={v => updateField('datos_operacion', 'exteriorizacion_voluntaria', v)} />
          <Field label="Conoce Delito Precedente" value={getVal('datos_operacion', 'conoce_delito_precedente')} editing={editing} onChange={v => updateField('datos_operacion', 'conoce_delito_precedente', v)} />
        </div>
      </Section>

      {/* Persona Física */}
      {hasPF && (
        <Section title="Persona Física" icon={User}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['nombre', 'Nombre'], ['apellido', 'Apellido'], ['cuit_cdi', 'CUIT/CDI'], ['nro_documento', 'Nro Documento'],
              ['tipo_documento', 'Tipo Documento'], ['fecha_nacimiento', 'F. Nacimiento'], ['sexo', 'Sexo'], ['nacionalidad', 'Nacionalidad'],
              ['calle', 'Calle'], ['nro', 'Nro'], ['piso', 'Piso'], ['departamento', 'Depto'],
              ['localidad', 'Localidad'], ['provincia', 'Provincia'], ['codigo_postal', 'CP'], ['pais', 'País'],
              ['email', 'Email'], ['actividad', 'Actividad'], ['estado_civil', 'Estado Civil'], ['relacion_hecho', 'Relación Hecho'],
              ['paraiso_fiscal', 'Paraíso Fiscal'], ['triple_frontera', 'Triple Frontera'],
            ].map(([k, label]) => (
              <Field key={k} label={label} value={getVal('persona_fisica', k)} editing={editing} onChange={v => updateField('persona_fisica', k, v)} />
            ))}
          </div>
        </Section>
      )}

      {/* Persona Jurídica */}
      {hasPJ && (
        <Section title="Persona Jurídica" icon={Building2}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['denominacion', 'Denominación'], ['cuit_cdi', 'CUIT/CDI'], ['calle', 'Calle'], ['nro', 'Nro'],
              ['localidad', 'Localidad'], ['provincia', 'Provincia'], ['codigo_postal', 'CP'], ['pais', 'País'],
              ['email', 'Email'], ['actividad', 'Actividad'], ['relacion_hecho', 'Relación Hecho'],
            ].map(([k, label]) => (
              <Field key={k} label={label} value={getVal('persona_juridica', k)} editing={editing} onChange={v => updateField('persona_juridica', k, v)} />
            ))}
          </div>
        </Section>
      )}

      {/* Persona Física Extranjera */}
      {hasPFE && (
        <Section title="Persona Física Extranjera" icon={User}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              ['apellido', 'Apellido'], ['nombre', 'Nombre'], ['tipo_identificador_tributario', 'Tipo ID Tributario'],
              ['nro_identificacion_tributaria', 'Nro ID Tributaria'], ['nacionalidad', 'Nacionalidad'], ['pais', 'País'],
            ].map(([k, label]) => (
              <Field key={k} label={label} value={getVal('persona_fisica_extranjera', k)} editing={editing} onChange={v => updateField('persona_fisica_extranjera', k, v)} />
            ))}
          </div>
        </Section>
      )}

      {/* Operaciones y Productos */}
      <Section title="Operaciones y Productos" icon={DollarSign}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ['fecha_inicio', 'Fecha Inicio'], ['fecha_fin', 'Fecha Fin'], ['monto', 'Monto'], ['moneda', 'Moneda'],
            ['producto', 'Producto'], ['nro_identificacion', 'CVU/CBU'], ['tipo_inusualidad', 'Tipo Inusualidad'],
            ['localidad_1', 'Localidad'], ['provincia_1', 'Provincia'], ['pais_1', 'País'],
            ['relacion_producto_hecho', 'Relación Producto/Hecho'],
          ].map(([k, label]) => (
            <Field key={k} label={label} value={String(getVal('operaciones_productos', k) || '')} editing={editing} onChange={v => updateField('operaciones_productos', k, v)} />
          ))}
        </div>
      </Section>

      {/* Textos del ROS */}
      <Section title="Textos del ROS" icon={FileText}>
        <div className="space-y-4">
          {[
            ['descripcion_operatoria', 'Descripción Operatoria'],
            ['analisis_so', 'Análisis del Sujeto Obligado'],
            ['documentacion_respaldo', 'Documentación de Respaldo'],
            ['conclusiones', 'Conclusiones'],
          ].map(([k, label]) => (
            <Field key={k} label={label} value={getVal('textos', k)} editing={editing} onChange={v => updateField('textos', k, v)} type="textarea" />
          ))}
        </div>
      </Section>

      {/* Historial */}
      <Section title="Historial" icon={Clock}>
        <div className="space-y-2">
          {(ros.historial || []).map((h, i) => (
            <div key={i} className="flex items-center gap-3 text-sm py-1 border-b border-slate-100 last:border-0">
              <Badge variant="outline" className="text-xs shrink-0">{h.accion}</Badge>
              <span className="text-slate-600 flex-1">{h.detalle}</span>
              <span className="text-xs text-slate-400">{h.usuario_email}</span>
              <span className="text-xs text-slate-400">{new Date(h.fecha).toLocaleString('es-AR')}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
